/* eslint-disable no-bitwise -- required  */

// Function to convert hex color to RGBA
export function hexToRgba(hex: string): [number, number, number, number] {
  // Remove the leading '#' if present
  let normalizedHex = hex.replace(/^#/, '');

  // Convert 3-digit hex to 6-digit hex
  if (normalizedHex.length === 3) {
    normalizedHex = normalizedHex
      .split('')
      .map((hexChar: string) => hexChar + hexChar)
      .join('');
  }

  // Parse the hex color into its RGB components
  // #RRGGBBAA
  if (normalizedHex.length === 8) {
    const bigint = parseInt(normalizedHex, 16);
    const r = (bigint >> 24) & 255;
    const g = (bigint >> 16) & 255;
    const b = (bigint >> 8) & 255;
    const a = (bigint & 255) / 255;
    return [r, g, b, a];
  }

  // Parse the hex color into its RGBA components
  // #RRGGBB
  const bigint = parseInt(normalizedHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return [r, g, b, 1];
}

// Formats a `#RRGGBB` / `#RRGGBBAA` hex string as a canonical
// `rgba(r, g, b, a)` declaration. Returns undefined when input is not a hex
// shape we recognise or `hexToRgba` produces NaN tuples on malformed input —
// callers fall back to a default rather than emitting `rgba(NaN, ...)`. Shared
// between the web and native effects renderers so both emit byte-identical
// shadow colors.
export function hexToRgbaString(raw: string): string | undefined {
  if (!raw.startsWith('#')) return undefined;
  const hexBodyLength = raw.length - 1;
  if (hexBodyLength !== 6 && hexBodyLength !== 8) return undefined;
  const [r, g, b, a] = hexToRgba(raw);
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) return undefined;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

// Function to calculate relative luminance
function luminance(r: number, g: number, b: number, a: number): number {
  const rgb = [r, g, b].map((value: number) => {
    const transformedValue = value / 255;
    return transformedValue <= 0.03928
      ? transformedValue / 12.92
      : ((transformedValue + 0.055) / 1.055) ** 2.4;
  });

  // Luminance of the color
  const colorLuminance = 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];

  // Blend with white background using alpha
  // Luminance = a * colorLuminance + (1 - a) * 1 (white background)
  return a * colorLuminance + (1 - a) * 1;
}

// Function to calculate contrast ratio
export function contrastRatio(hex1: string, hex2: string): number {
  const rgb1 = hexToRgba(hex1);
  const rgb2 = hexToRgba(hex2);

  const lum1 = luminance(rgb1[0], rgb1[1], rgb1[2], rgb1[3]) + 0.05;
  const lum2 = luminance(rgb2[0], rgb2[1], rgb2[2], rgb2[3]) + 0.05;

  return lum1 > lum2 ? lum1 / lum2 : lum2 / lum1;
}

// Function to check if the contrast ratio meets WCAG AA minimum for normal text
export function isContrastAccessible(contrast: number): boolean {
  return contrast >= 4.5; // WCAG AA standard for normal text
}

/**
 * Converts a RGB color string to hexadecimal format
 * @param rgbStr - RGB color string (e.g., "rgb(12,33,45)" or "rgb(12, 33, 45)")
 * @returns string- Hexadecimal color string (e.g., "#0C212D")
 */
const RGB_REGEX = /rgb\(\s*(?<temp1>\d+)\s*,\s*(?<temp2>\d+)\s*,\s*(?<temp3>\d+)\s*\)/i;
export function rgbToHex(rgbStr: string): string {
  // Extract the RGB values using regex

  const match = RGB_REGEX.exec(rgbStr);

  if (!match) {
    throw new Error('Invalid RGB format. Expected format: rgb(r,g,b)');
  }

  // Parse the RGB values as integers
  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);

  // Ensure values are within valid range (0-255)
  const validR = Math.max(0, Math.min(255, r));
  const validG = Math.max(0, Math.min(255, g));
  const validB = Math.max(0, Math.min(255, b));

  // Convert to hex and pad with zeros if needed
  const hexR = validR.toString(16).padStart(2, '0');
  const hexG = validG.toString(16).padStart(2, '0');
  const hexB = validB.toString(16).padStart(2, '0');

  // Return the hex color string
  return `#${hexR}${hexG}${hexB}`.toUpperCase();
}
